import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { AlertService } from './alert.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {

  readonly api = environment.useEmulator
    ? 'http://127.0.0.1:5001/' + environment.firebaseConfig.projectId + '/us-central1/app'
    : 'https://us-central1-' + environment.firebaseConfig.projectId + '.cloudfunctions.net/app';
  // readonly api = 'http://127.0.0.1:5001/hospappwebsite/us-central1/app'

  constructor(public afs: AngularFirestore,
              public router: Router,
              public location: Location,
              public http: HttpClient,
              private alertService: AlertService,
              private toastService: ToastService) { }

  async deleteUserHandler(userObject: any) {
    const result = await this.alertService.open({
      heading: 'Delete Account',
      description: `Are you sure you want to permanently delete the account for "${userObject.businessName || userObject.firstName + ' ' + userObject.lastName || 'this user'}"? This action cannot be undone and will remove all associated data.`,
      buttons: [
        {
          label: 'Cancel',
          color: 'neutral',
          icon: 'fa-times'
        },
        {
          label: 'Delete Account',
          color: 'danger',
          icon: 'fa-trash',
          handler: async () => {
            try {
              // Delete auth user if they have authentication
              console.log('Deleting auth user with UID:', userObject.uid);
              if (!userObject.noAuth) {
                await this.deleteUser(userObject.uid);
              }
              
              // Delete user data
              await this.deleteData(userObject.uid);
              
              // Show success message
              this.toastService.showSuccess('Account deleted successfully');
              
              // Navigate back only on success
              this.location.back();
            } catch (err: any) {
              console.error('Error deleting account:', err);
              this.toastService.showError(
                err?.error?.message || err?.message || 'Failed to delete account. Please try again.'
              );
            }
          }
        }
      ]
    });
  }
  deleteUser(uid: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/firebase/user/delete`;
      let sub = this.http.post<any>(url, {uid: uid}).subscribe(data => {
        sub.unsubscribe();
        resolve(null);
      }, err => {
        sub.unsubscribe();
        reject(err);
      });
    });
  }
  async deleteData(uid: string) {
    // Delete all user-related documents
    const deletePromises = [
      this.afs.doc('users/' + uid).delete(),
      // this.afs.doc('users/' + uid + '/customer/customer').delete(),
      // this.afs.doc('users/' + uid + '/profile/profile').delete(),
      // this.afs.doc('users/' + uid + '/appstoreDetails/appstoreDetails').delete(),
      // this.afs.doc('users/' + uid + '/starterTheme/starterTheme').delete(),
      // this.afs.doc('users/' + uid + '/usage/totals').delete(),
      // this.afs.doc('users/' + uid + '/config/config').delete(),
      // this.afs.doc('users/' + uid + '/config/display').delete(),
      // this.afs.doc('users/' + uid + '/config/service').delete(),
      // this.afs.doc('users/' + uid + '/password/password').delete()
    ];
    
    // Wait for all deletions to complete
    await Promise.all(deletePromises);
  }

  scheduleEmail(sid: string, uid: string, agentUid: string, test?: boolean) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/add/sequence` + (test ? '/test' : '');
      let sub = this.http.post(url,{
        sid:sid,
        uid:uid,
        agentUid:agentUid
      }).subscribe(data => {
        sub.unsubscribe();
        resolve(null);
      }, err => {
        reject(err)
      });
    });
  }

  forceSend(uid: string, seid: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/scheduled/forcesend`;
      let sub = this.http.post(url,{
        uid: uid,
        seid: seid
      }).subscribe(data => {
        sub.unsubscribe();
        resolve(null);
      }, err => {
        reject(err)
      });
    });
  }

  addEmailSender(senderId: string, agencyId: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/sengrid/senders/add`;
      let sub = this.http.post(url,{
        senderId:senderId,
        agencyId:agencyId,
      }).subscribe(data => {
        sub.unsubscribe();
        resolve(null);
      }, err => {
        reject(err)
      });
    });
  }
  deleteSender(senderId: string, agencyId: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/sengrid/senders/delete`;
      let sub = this.http.post(url,{
        senderId:senderId,
        agencyId:agencyId,
      }).subscribe(data => {
        sub.unsubscribe();
        resolve(null);
      }, err => {
        reject(err)
      });
    });
  }
  testEmail(senderId: string, agencyId: string, testEmailAddress: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/sengrid/senders/test`;
      let sub = this.http.post(url,{
        senderId:senderId,
        agencyId:agencyId,
        testEmailAddress:testEmailAddress
      }).subscribe(data => {
        sub.unsubscribe();
        resolve(null);
      }, err => {
        reject(err)
      });
    });
  }

  addSequenceToAgencySegment(aid: string, sid: string, arid: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/autoresponders/add`;
      let sub = this.http.post(url,{
        aid: aid,
        sid: sid,
        arid: arid
      }).subscribe(data => {
        console.log(data);
        sub.unsubscribe();
        resolve(null);
      }, err => {
        console.error(err);
        reject(err)
      });
    });
  }
  markEmailAsClicked(uid: string, eid: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/email/clicked`;
      let sub = this.http.post(url,{
        uid,
        eid
      }).subscribe(data => {
        sub.unsubscribe();
        resolve(null);
      }, err => {
        reject(err)
      });
    });
  }

  sendInviteEmail(aid: string, email: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/agency-invites/send`;
      let sub = this.http.post(url,{
        email: email,
        aid: aid,
      }).subscribe(data => {
        console.log(data);
        sub.unsubscribe();
        resolve(null);
      }, err => {
        console.error(err);
        reject(err)
      });
    });
  }

  createProspect(email: string, telephone?: string, firstName?: string, lastName?: string, agencyId?: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/user/createprospect`;
      let sub = this.http.post(url,{
        email: email,
        telephone: telephone,
        firstName: firstName,
        lastName: lastName,
        agencyId: agencyId
      }).subscribe(data => {
        console.log('User creation/fetch result:', data);
        sub.unsubscribe();
        resolve(data);
      }, err => {
        console.error('Error creating/fetching user:', err);
        reject(err)
      });
    });
  }

  syncAgencyIds(uid: string, email: string) {
    return new Promise((resolve, reject) => {
      const url = `${this.api}/agency/sync-ids`;
      let sub = this.http.post(url, {
        uid: uid,
        email: email
      }).subscribe(data => {
        console.log('Sync agency IDs result:', data);
        sub.unsubscribe();
        resolve(data);
      }, err => {
        console.error('Error syncing agency IDs:', err);
        reject(err);
      });
    });
  }
}
